import { CommonEventOptions } from '@westwing/appshell_types';
import { useState } from 'react';
import { SessionTypes, UserLoginDataResponse } from '../types/types';
import {
    FormFieldTracking,
    FormInteractions,
    FormLinkTracking,
    LoginType,
    OverlayActions,
    OverlayDismissLabel,
    OverlayShownLabel,
    OverlayType,
    PrivacyPolicyAcceptanceLabel,
    SIGNUP_AND_LOGIN_EVENT,
    SubmitAndLoginActions,
} from 'Interfaces/overlayTracking';
import { eventOptionsToString } from 'Helpers/trackingHelper';
import { useGaTracking } from 'AppShell/TrackingHelper';
import isClubMfPath from 'SharedHelpers/isClubMfPath';

export interface SignupFormTrackingHook {
    trackOverlayShown: (label: OverlayShownLabel) => void;
    trackRegistrationSuccess: (overlay: OverlayType, acceptNewsletter: boolean) => void;
    trackLoginSuccess: (overlay: OverlayType, userData?: UserLoginDataResponse) => void;
    trackLoginSuccessFromRegistration: (overlay: OverlayType) => void;
    trackLoginErrorWrongPassword: (overlay: OverlayType) => void;
    trackOverlayDismiss: (label: OverlayDismissLabel) => void;
    trackPrivacyPolicyShown: () => void;
    trackPrivacyPolicyAcceptance: (isTermsAccepted: boolean) => void;
    trackFieldFilled: (field: FormFieldTracking) => void;
    trackFieldError: (field: FormFieldTracking) => void;
    trackFieldChecked: (field: FormFieldTracking) => void;
    trackLinkClick: (link: FormLinkTracking) => void;
    trackFormUnfolded: () => void;
    trackPrivacyPolicyScroll: () => void;
    trackFormSubmit: (isSubmit?: boolean) => void;
    trackLegalTextExpand: (isSubmit?: boolean) => void;
}

export const enum trackingCategories {
    CHECKOUT = 'Checkout',
    SHOP_TO_CLUB = 'Promotional Signup Overlay',
    LOGIN_OVERLAY = 'Login Overlay',
    SIGNUP_OVERLAY = 'Signup Overlay',
}

const useSignupFormTracking = (): SignupFormTrackingHook => {
    const { trackGenericEvent } = useGaTracking();
    const isClub = isClubMfPath();

    const [trackedEvents, setTrackedEvents] = useState<{ [event: string]: boolean }>({});

    const trackEventOnce = (category: trackingCategories, options: CommonEventOptions) => {
        const eventHash = `${category}.${eventOptionsToString(options)}`;
        if (!trackedEvents[eventHash]) {
            trackGenericEvent(category, options);
            const nextEvents = trackedEvents;
            nextEvents[eventHash] = true;
            setTrackedEvents(nextEvents);
        }
    };

    const trackRegistrationSuccess = (overlay: OverlayType, acceptNewsletter: boolean) => {
        if (overlay === OverlayType.Checkout) {
            trackEventOnce(trackingCategories.CHECKOUT, {
                action: 'Registration',
                label: acceptNewsletter ? 'With newsletter subscription' : 'Without newsletter subscription',
            });
        } else if (overlay === OverlayType.ShopToClubOverlay) {
            trackEventOnce(trackingCategories.SHOP_TO_CLUB, {
                action: 'Registration',
                label: 'Registration',
            });
        } else if (overlay === OverlayType.SignupOverlay) {
            trackGenericEvent(trackingCategories.SIGNUP_OVERLAY, {
                action: 'Signup Successful',
                label: acceptNewsletter
                    ? 'Registration with newsletter subscription'
                    : 'Registration without newsletter subscription',
            });
        }
    };

    const trackLoginSuccess = (
        overlay: OverlayType = OverlayType.ShopToClubOverlay,
        userData?: UserLoginDataResponse
    ) => {
        if (overlay === OverlayType.Checkout) {
            trackEventOnce(trackingCategories.CHECKOUT, {
                action: 'Login',
                label: 'Login',
            });
        } else if (overlay === OverlayType.ShopToClubOverlay) {
            trackEventOnce(trackingCategories.SHOP_TO_CLUB, {
                action: 'Login',
                label: 'Login',
            });
        } else if (overlay === OverlayType.LoginOverlay) {
            trackGenericEvent(trackingCategories.LOGIN_OVERLAY, {
                action: 'Login Successfully',
            });
        }
        if (userData && overlay === OverlayType.ShopToClubOverlay) {
            trackEventOnce(trackingCategories.SHOP_TO_CLUB, {
                event: SIGNUP_AND_LOGIN_EVENT,
                action: SubmitAndLoginActions.Login,
                label:
                    userData?.sessionType === SessionTypes.partial ? LoginType.ShopPartialLogin : LoginType.ShopLogin,
            });
        }
    };

    const trackLoginSuccessFromRegistration = (overlay: OverlayType) => {
        trackLoginSuccess(overlay);
    };

    const trackLoginErrorWrongPassword = (overlay: OverlayType) => {
        if (overlay === OverlayType.Checkout) {
            trackEventOnce(trackingCategories.CHECKOUT, {
                action: 'Login',
                label: 'Correct email but wrong password',
            });
        } else {
            trackEventOnce(trackingCategories.SHOP_TO_CLUB, {
                action: 'Login',
                label: 'Correct email but wrong password',
            });
        }
    };

    const trackOverlayDismiss = (label: OverlayDismissLabel) => {
        if (isClub) {
            trackGenericEvent(trackingCategories.SIGNUP_OVERLAY, {
                action: 'Signup Form Dismissed',
                label,
            });
            return;
        }

        trackEventOnce(trackingCategories.SHOP_TO_CLUB, {
            event: SIGNUP_AND_LOGIN_EVENT,
            action: 'Signup overlay dismissed',
            label,
        });
    };

    const trackPrivacyPolicyShown = () => {
        trackEventOnce(trackingCategories.SHOP_TO_CLUB, {
            event: SIGNUP_AND_LOGIN_EVENT,
            action: OverlayActions.PrivacyPolicyShown,
            label: OverlayActions.PrivacyPolicyShown,
        });
    };

    const trackPrivacyPolicyAcceptance = (isClubNewsletterSubscribed: boolean) => {
        const label: PrivacyPolicyAcceptanceLabel = isClubNewsletterSubscribed ? 'Accepted' : 'Not accepted';
        trackEventOnce(trackingCategories.SHOP_TO_CLUB, {
            event: SIGNUP_AND_LOGIN_EVENT,
            action: OverlayActions.PrivacyPolicyAcceptance,
            label,
        });
    };

    const trackFieldFilled = (field: FormFieldTracking) => {
        trackEventOnce(trackingCategories.SHOP_TO_CLUB, {
            event: SIGNUP_AND_LOGIN_EVENT,
            action: OverlayActions.FormFieldChanged,
            label: field,
        });
    };

    const trackFieldError = (field: FormFieldTracking) => {
        trackEventOnce(trackingCategories.SHOP_TO_CLUB, {
            event: SIGNUP_AND_LOGIN_EVENT,
            action: OverlayActions.FormFieldError,
            label: field,
        });
    };

    const trackFieldChecked = (field: FormFieldTracking) => {
        trackEventOnce(trackingCategories.SHOP_TO_CLUB, {
            event: SIGNUP_AND_LOGIN_EVENT,
            action: OverlayActions.FormFieldChanged,
            label: field,
        });
    };

    const trackLinkClick = (link: FormLinkTracking) => {
        trackEventOnce(trackingCategories.SHOP_TO_CLUB, {
            event: SIGNUP_AND_LOGIN_EVENT,
            action: OverlayActions.FormLinkClicked,
            label: link,
        });
    };

    const trackFormUnfolded = () => {
        trackEventOnce(trackingCategories.SHOP_TO_CLUB, {
            event: SIGNUP_AND_LOGIN_EVENT,
            action: OverlayActions.FormInteraction,
            label: FormInteractions.FormUnfolded,
        });
    };

    const trackPrivacyPolicyScroll = () => {
        trackEventOnce(trackingCategories.SHOP_TO_CLUB, {
            event: SIGNUP_AND_LOGIN_EVENT,
            action: OverlayActions.FormInteraction,
            label: FormInteractions.PrivacyPolicyScrolled,
        });
    };

    const trackFormSubmit = (isSubmit = true) => {
        trackEventOnce(trackingCategories.SHOP_TO_CLUB, {
            event: SIGNUP_AND_LOGIN_EVENT,
            action: OverlayActions.FormInteraction,
            label: isSubmit ? FormInteractions.SubmitButtonClicked : FormInteractions.ContinueButtonClicked,
        });
    };

    const trackLegalTextExpand = () => {
        trackEventOnce(trackingCategories.SHOP_TO_CLUB, {
            event: SIGNUP_AND_LOGIN_EVENT,
            action: OverlayActions.FormInteraction,
            label: FormInteractions.LegalTextExpanded,
        });
    };

    const trackOverlayShown = (label: OverlayShownLabel) => {
        if (isClub) {
            trackGenericEvent(trackingCategories.SIGNUP_OVERLAY, {
                action: 'Signup Form Impression',
            });
            return;
        }
        trackEventOnce(trackingCategories.SHOP_TO_CLUB, {
            event: SIGNUP_AND_LOGIN_EVENT,
            action: 'Signup overlay impression',
            label,
        });
    };

    return {
        trackRegistrationSuccess,
        trackLoginSuccess,
        trackLoginSuccessFromRegistration,
        trackLoginErrorWrongPassword,
        trackOverlayDismiss,
        trackOverlayShown,
        trackPrivacyPolicyShown,
        trackPrivacyPolicyAcceptance,
        trackFieldFilled,
        trackFieldError,
        trackFieldChecked,
        trackLinkClick,
        trackFormUnfolded,
        trackPrivacyPolicyScroll,
        trackFormSubmit,
        trackLegalTextExpand,
    };
};

export default useSignupFormTracking;
